"use client";
import {
  BMPrimitives,
  BMText,
  useBMLocalizeTypography,
  useBMTheme,
} from "@traveloka/district-bloom";
import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import BMView from "../../View/BMView";

export type ChartTooltipProps = TooltipProps<number, string>;

export type BarSeries = {
  key: string;
  color: string;
  label: string;
};

type Props = {
  data: Record<string, string | number>[];
  series: BarSeries[];
  legendItems?: BarSeries[];
  getCellFill?: (
    dataKey: string,
    row: Record<string, string | number>,
    rowIndex: number
  ) => string | undefined;
  note?: string;
  yAxisWidth?: number;
  yAxisTicks?: number[];
  yAxisDomain?: [number | string, number | string];
  formatYAxisTick?: (v: string | number) => string;
  formatXAxisTick?: (v: string | number) => string;
  renderTooltip?: (props: ChartTooltipProps) => React.ReactElement | null;
  showLegend?: boolean;
};

export default function AnalyticsBarChart({
  data,
  series,
  legendItems,
  getCellFill,
  note,
  yAxisTicks,
  yAxisDomain,
  yAxisWidth = 25,
  formatYAxisTick,
  formatXAxisTick,
  renderTooltip,
  showLegend = false,
}: Props) {
  const { BMComponents } = useBMTheme();

  const xAxisInterval = useMemo(
    () => (data.length > 13 ? 2 : 0),
    [data.length]
  );

  const legendRows = useMemo(() => {
    if (!showLegend) return [];
    const items = legendItems ?? series;
    const rows: BarSeries[][] = [];
    for (let i = 0; i < items.length; i += 2) {
      rows.push(items.slice(i, i + 2));
    }
    return rows;
  }, [series, legendItems, showLegend]);

  return (
    <BMView flex style={styles.container}>
      <ResponsiveContainer width="100%" minHeight={200}>
        <BarChart
          data={data}
          barGap={2}
          barCategoryGap="24%"
          margin={{
            top: BMPrimitives.BMSize.xs,
            right: BMPrimitives.BMSize.xl2,
            bottom: BMPrimitives.BMSize.xs,
            left: BMPrimitives.BMSize.lg,
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={BMComponents.BMStyleLineBordercolorSubtle}
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={<AxisTick formatter={formatXAxisTick} />}
            axisLine={false}
            tickLine={false}
            interval={xAxisInterval}
          />
          <YAxis
            tick={<AxisTick formatter={formatYAxisTick} />}
            axisLine={false}
            tickLine={false}
            ticks={yAxisTicks}
            width={yAxisWidth}
            domain={yAxisDomain}
          />
          {renderTooltip ? (
            <Tooltip content={renderTooltip} cursor={false} />
          ) : null}
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              radius={[
                BMPrimitives.BMRadius.sm,
                BMPrimitives.BMRadius.sm,
                0,
                0,
              ]}
            >
              {getCellFill
                ? data.map((row, i) => (
                    <Cell
                      key={i}
                      fill={getCellFill(s.key, row, i) ?? s.color}
                    />
                  ))
                : null}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      {note || showLegend ? (
        <BMView style={styles.footer} spacing="sm">
          {note ? (
            <BMText variant="uiNormalLi" ink="BMStyleTextInkSecondary">
              {note}
            </BMText>
          ) : null}
          {legendRows.length > 0 ? (
            <BMView spacing="xs">
              {legendRows.map((row, i) => (
                <BMView key={i} row spacing="lg">
                  {row.map((s) => (
                    <BMView key={s.key} flex row align="center" spacing="xs">
                      <BMView
                        style={[styles.square, { backgroundColor: s.color }]}
                      />
                      <BMText variant="uiNormalLi" style={{ color: s.color }}>
                        {s.label}
                      </BMText>
                    </BMView>
                  ))}
                </BMView>
              ))}
            </BMView>
          ) : null}
        </BMView>
      ) : null}
    </BMView>
  );
}

type AxisTickProps = {
  x: number;
  y: number;
  payload: { value: string | number };
  textAnchor?: "middle" | "start" | "end" | "inherit";
  formatter?: (v: string | number) => string;
};

function isAxisTickProps(
  props: Record<string, unknown>
): props is AxisTickProps {
  const payload = props.payload;
  if (typeof props.x !== "number") return false;
  if (typeof props.y !== "number") return false;
  if (typeof payload !== "object" || payload === null) return false;
  return "value" in payload;
}

function AxisTick(props: Record<string, unknown>) {
  const { BMComponents } = useBMTheme();
  const { BMTypography } = useBMLocalizeTypography();
  if (!isAxisTickProps(props)) return null;
  const { x, y, payload, textAnchor, formatter } = props;
  const label = formatter ? formatter(payload.value) : String(payload.value);
  if (!label) return null;
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor={textAnchor ?? "middle"}
      fill={BMComponents.BMStyleTextInkMuted}
      {...BMTypography.uiSmallLi}
    >
      {label}
    </text>
  );
}

const styles = StyleSheet.create({
  container: {
    minWidth: 0,
  },
  footer: {
    marginTop: BMPrimitives.BMSize.sm,
    paddingHorizontal: BMPrimitives.BMSize.md,
  },
  square: {
    width: BMPrimitives.BMSize.sm,
    height: BMPrimitives.BMSize.sm,
  },
});
